import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Download, Shield, CreditCard, Search, Camera } from 'lucide-react';
import AppStoreRedirect from './AppStoreRedirect';

const BlogList = lazy(() => import('./pages/blog'));
const BlogPostPage = lazy(() => import('./pages/blog/BlogPostPage'));
const TagPage = lazy(() => import('./pages/blog/TagPage'));
const CategoryPage = lazy(() => import('./pages/blog/CategoryPage'));

const BASE_APP_STORE_URL = "https://apps.apple.com/app/id6738414239?pt=1048578458&mt=8&utm_source=website&utm_campaign=downloads";

const getAppStoreUrl = (source, medium, incomingCt = null) => {
  const finalSource = incomingCt ? `${source}-${incomingCt}` : source;
  return `${BASE_APP_STORE_URL}&ct=${finalSource}&utm_medium=${medium}`;
};

const WalletBuddyLanding = () => {
  const [activeFeature, setActiveFeature] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isBottomButtonVisible, setIsBottomButtonVisible] = useState(false);
  const bottomButtonRef = useRef(null);

  // Get ct parameter from URL
  const urlParams = new URLSearchParams(window.location.search);
  const incomingCt = urlParams.get('ct');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsBottomButtonVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    const currentRef = bottomButtonRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const features = [
    {
      icon: <CreditCard className="w-12 h-12 text-custom-blue" />,
      title: "Comprehensive Card Management",
      description: "Add, edit, and organize all your cards in one secure place. Scan cards details automatically."
    },
    {
      icon: <Search className="w-12 h-12 text-custom-blue" />,
      title: "Powerful Search & Filters",
      description: "Find any card instantly with smart search and intelligent filters."
    },
    {
      icon: <Shield className="w-12 h-12 text-custom-blue" />,
      title: "Local & Encrypted Storage",
      description: "Your card data stays safe, stored locally and backed up securely."
    }
  ];

  const screenshots = [
    {
      src: `${process.env.PUBLIC_URL}/assets/images/main-card-management-screenshot.webp`,
      alt: `Wallet Buddy Screenshot 1`,
      name: "Main Card Management Interface",
      description: "Main card management interface - track all your cards"
    },
    {
      src: `${process.env.PUBLIC_URL}/assets/images/tap-to-copy-card-details-feature-screenshot.webp`,
      alt: `Wallet Buddy Screenshot 2`,
      name: "Tap to Copy Card Details",
      description: "Tap to expand card and copy key info"
    },
    {
      src: `${process.env.PUBLIC_URL}/assets/images/card-quick-actions-screenshot.webp`,
      alt: `Wallet Buddy Screenshot 3`,
      name: "Card Quick Actions",
      description: "Perform quick actions on cards"
    },
    {
      src: `${process.env.PUBLIC_URL}/assets/images/customizable-grid-view-screenshot.webp`,
      alt: `Wallet Buddy Screenshot 4`,
      name: "Customizable Grid View",
      description: "View cards in a customizable grid"
    },
    {
      src: `${process.env.PUBLIC_URL}/assets/images/card-scanner-feature-screenshot.webp`,
      alt: `Wallet Buddy Screenshot 5`,
      name: "Card Scanner Feature",
      description: "Scan in cards with auto perspective correction and auto populate card details - built-in scanner using OCR technology"
    },
    {
      src: `${process.env.PUBLIC_URL}/assets/images/smart-fuzzy-search-screenshot.webp`,
      alt: `Wallet Buddy Screenshot 6`,
      name: "Smart Search Feature",
      description: "Fuzzy search cards by name, issuer, card number, last 4/5 digits, and tags"
    },
    {
      src: `${process.env.PUBLIC_URL}/assets/images/advanced-card-filtering-screenshot.webp`,
      alt: `Wallet Buddy Screenshot 7`,
      name: "Advanced Card Filtering",
      description: "Filter cards by card type, network, and bonus categories"
    },
    {
      src: `${process.env.PUBLIC_URL}/assets/images/card-sorting-options-screenshot.webp`,
      alt: `Wallet Buddy Screenshot 8`,
      name: "Card Sorting Options",
      description: "Sort cards by various options"
    },
    {
      src: `${process.env.PUBLIC_URL}/assets/images/payment-reminders-feature-screenshot.webp`,
      alt: `Wallet Buddy Screenshot 9`,
      name: "Payment Reminders",
      description: "Stay up to date on cards without autopay by setting payment reminders"
    },
    {
      src: `${process.env.PUBLIC_URL}/assets/images/card-churning-statistics-screenshot.webp`,
      alt: `Wallet Buddy Screenshot 10`,
      name: "Card Statistics",
      description: "Show custom display settings and card statistics (Chase 5/24 rule for churning)"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <header className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 backdrop-blur-md shadow-md py-2' : 'bg-transparent py-4'
      }`}>
        <div className="container mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <a 
              href={getAppStoreUrl('WebsiteHeaderLogo', 'internal', incomingCt)}
              className="transform transition-all duration-300 hover:-translate-y-1 hover:scale-105"
            >
              <img src={`${process.env.PUBLIC_URL}/assets/images/wallet-buddy-app-icon.webp`} alt="Logo" className="w-8 h-8 rounded-lg" />
            </a>
            <span className="font-bold text-xl text-gray-900">Wallet Buddy!</span>
          </div>
          <a 
            href={getAppStoreUrl('WebsiteHeaderBadge', 'internal', incomingCt)}
            className=""
          >
              <img 
                src={`${process.env.PUBLIC_URL}/assets/images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg`}
                alt="Download on the App Store"
                className="h-10 w-32"
              />
          </a>
        </div>
      </header>

      <div className="container mx-auto px-4 pt-28 md:pt-32 text-center">
        <div className="flex flex-col items-center">
          <a 
            href={getAppStoreUrl('WebsiteMainIcon', 'internal', incomingCt)}
            className="transform transition-all duration-300 hover:-translate-y-1 hover:scale-105 shine-effect-icon"
          >
            <img 
              src={`${process.env.PUBLIC_URL}/assets/images/wallet-buddy-app-icon.webp`} 
              alt="Wallet Buddy App Icon" 
              className="w-24 md:w-36 h-24 md:h-36 object-cover rounded-2xl shadow-lg mb-8 float-animation"
            />
          </a>
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-custom-orange1 to-custom-orange2 bg-clip-text text-transparent shine-effect print:text-custom-orange1">
              Wallet Buddy!
            </h1>
            <p className="text-xl md:text-2xl font-medium text-gray-600 tracking-wide -mt-0 mb-6 shine-effect">
              Your Digital Card Management App
            </p>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto px-4">
              Simplify your life by managing all your cards securely, efficiently, and elegantly 
              <span className="hidden md:inline">—all in one beautiful app</span>.
            </p>
            
            <div className="flex flex-col items-center justify-center gap-0 mt-8">
              <a 
                href={getAppStoreUrl('WebsiteMainCTA', 'cta', incomingCt)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="shine-effect bg-gradient-to-r from-custom-orange1 to-custom-orange2 text-white font-bold py-4 px-8 rounded-full flex items-center transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105"
              >
                <Download className="mr-2 w-5 h-5" /> Download on the App Store
              </a>
              <Link 
                to="/blog" 
                className="group text-gray-600 hover:text-custom-orange1 font-semibold py-4 px-8 rounded-full flex items-center transition-all duration-300"
              >
                Read Our Blog 
                <svg 
                  className="ml-2 w-4 h-4 transform transition-transform group-hover:translate-x-1" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
          Features That Make a Difference
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-lg transition duration-300 transform hover:scale-102 ${
                activeFeature === index ? 'border-2 border-blue-500' : ''
              }`}
              onMouseEnter={() => setActiveFeature(index)}
              onMouseLeave={() => setActiveFeature(null)}
            >
              <div className="flex flex-col items-center text-center mb-4">
                {feature.icon}
                <h3 className="mt-4 text-xl font-semibold text-gray-800">{feature.title}</h3>
              </div>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
          Experience Wallet Buddy!
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
          {screenshots.map((screenshot, index) => (
            <div 
              key={index} 
              className="relative aspect-[9/19.5] rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <img
                src={screenshot.src}
                alt={`${screenshot.name} - ${screenshot.description}`}
                className="w-full h-full object-cover"
                loading={index < 2 ? "eager" : "lazy"}
                width="390"
                height="844"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-50/80 backdrop-blur-sm py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Why Choose Wallet Buddy!
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div 
              className={`bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-lg transition duration-300 transform hover:scale-102 ${
                activeFeature === 3 ? 'border-2 border-blue-500' : ''
              }`}
              onMouseEnter={() => setActiveFeature(3)}
              onMouseLeave={() => setActiveFeature(null)}
            >
              <div className="flex flex-col items-center text-center mb-4">
                <Search className="w-12 h-12 text-custom-blue" />
                <h3 className="mt-4 text-xl font-semibold text-gray-800">Easy to Use</h3>
              </div>
              <p className="text-gray-600">
                Intuitive interface with powerful search, filters, and quick access to your cards.
              </p>
            </div>

            <div 
              className={`bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-lg transition duration-300 transform hover:scale-102 ${
                activeFeature === 4 ? 'border-2 border-blue-500' : ''
              }`}
              onMouseEnter={() => setActiveFeature(4)}
              onMouseLeave={() => setActiveFeature(null)}
            >
              <div className="flex flex-col items-center text-center mb-4">
                <Camera className="w-12 h-12 text-custom-blue" />
                <h3 className="mt-4 text-xl font-semibold text-gray-800">Effortless Card Entry</h3>
              </div>
              <p className="text-gray-600">
                Our advanced scanner automatically detects card numbers, expiration dates, and CVV codes, making adding new cards a breeze. Card images are perspective corrected, making cards look sharp.
              </p>
            </div>

            <div 
              className={`bg-white/70 backdrop-blur-sm p-6 rounded-2xl shadow-lg transition duration-300 transform hover:scale-102 ${
                activeFeature === 5 ? 'border-2 border-blue-500' : ''
              }`}
              onMouseEnter={() => setActiveFeature(5)}
              onMouseLeave={() => setActiveFeature(null)}
            >
              <div className="flex flex-col items-center text-center mb-4">
                <Shield className="w-12 h-12 text-custom-blue" />
                <h3 className="mt-4 text-xl font-semibold text-gray-800">Secure & Private</h3>
              </div>
              <p className="text-gray-600">
                Your card data is stored locally on your device, ensuring maximum privacy and security. Save your backups anywhere you want.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-custom-orange1 to-custom-orange2 text-white py-16 text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Take Control of Your Cards Today
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            Download Wallet Buddy! and experience a smarter way to manage your wallet
          </p>
          <a 
            ref={bottomButtonRef}
            href={getAppStoreUrl('WebsiteBottomCTA', 'cta', incomingCt)} 
            target="_blank" 
            rel="noopener noreferrer"
            className={`bg-white text-custom-orange1 hover:bg-blue-50 font-bold py-3 px-8 rounded-full inline-flex items-center transition duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 ${
              isBottomButtonVisible ? 'shine-effect-orange' : ''
            }`}
          >
            <Download className="mr-2" /> Get Wallet Buddy!
          </a>
        </div>
      </div>

      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">© 2024 Kevin Tang. All Rights Reserved. "Wallet Buddy!" is a trademark of Kevin Tang.</p>
          <div className="mt-4 space-x-4">
            <p className="text-gray-400">All trademarks and brand names belong to their respective owners and do not represent endorsements of any kind.</p>
          </div>
          <div className="mt-4 space-x-4">
            <Link to="/privacy" className="text-gray-400 hover:text-white transition">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-white transition">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

const PrivacyPolicy = lazy(() => import('./privacy_policy'));
const TermsOfService = lazy(() => import('./terms_of_service'));
// const SocialPreview = lazy(() => import('./SocialPreview'));

const NotFound = lazy(() => import('./NotFound'));

const App = () => {
  return (
    <Router>
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-xl">Loading...</div>
        </div>
      }>
        <Routes>
          <Route path="/" element={
            <WalletBuddyLanding />
          } />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/download" element={<AppStoreRedirect />} />
          {/* <Route path="/social-preview" element={<SocialPreview />} /> */}
          <Route path="/blog/tag/:tag" element={
            <Suspense fallback={<div>Loading...</div>}>
              <TagPage />
            </Suspense>
          } />
          <Route path="/blog/category/:category" element={
            <Suspense fallback={<div>Loading...</div>}>
              <CategoryPage />
            </Suspense>
          } />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;
